export interface TemplateColumn {
  name: string;
  color: string;
  colorBg: string;
  description: string;
  exampleCards: string[];
}

export interface TemplateDetail {
  slug: string;
  name: string;
  icon: string;
  tagline: string;
  description: string;
  bestFor: string[];
  columns: TemplateColumn[];
  aiSummary: string;
  seoTitle: string;
  seoDescription: string;
  relatedBlogSlug?: string;
  relatedBlogTitle?: string;
}

export const templateDetails: TemplateDetail[] = [
  // ─────────────────────────────────────────────────────────────────
  // Start Stop Continue
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'start-stop-continue',
    name: 'Start, Stop, Continue',
    icon: '🚦',
    tagline: 'The most actionable retrospective format. Three columns, zero ambiguity.',
    description:
      'Start, Stop, Continue is the most widely used retrospective template in agile teams. Its simplicity is its strength: every card written on the board maps directly to a behavior change. "Start" items become action items by default. "Stop" items are explicit commitments to drop something. "Continue" items protect what\'s working.',
    bestFor: [
      'Teams new to retrospectives — lowest barrier to participation',
      'Any sprint, any team — the format works universally',
      'When you want action items to emerge naturally from the exercise',
      'Fast-paced sessions — can be run effectively in under 60 minutes',
      'Remote teams who need a clear, structured format',
    ],
    columns: [
      {
        name: 'Start Doing',
        color: '#10B981',
        colorBg: '#D1FAE5',
        description: 'New practices, behaviors, or habits the team should adopt this sprint.',
        exampleCards: [
          'Writing PR descriptions before requesting review',
          'Holding a 30-min design review before dev starts each feature',
          'Async standups on Wednesdays to reduce meeting load',
          'Using feature flags for new releases to reduce deployment risk',
          'Sharing learnings in a #team-learnings Slack channel',
        ],
      },
      {
        name: 'Stop Doing',
        color: '#EF4444',
        colorBg: '#FEE2E2',
        description: 'Practices that are hurting the team and should be explicitly dropped.',
        exampleCards: [
          'Merging to main without at least one code review',
          'Starting tickets before acceptance criteria are defined',
          'Over-committing in sprint planning — we\'re at 120% capacity again',
          'Skipping standups on Fridays — creates weekend coordination gaps',
          'Pinging reviewers directly instead of using GitHub review requests',
        ],
      },
      {
        name: 'Continue Doing',
        color: '#F59E0B',
        colorBg: '#FEF3C7',
        description: 'Things that are working well and should be actively maintained.',
        exampleCards: [
          'Thursday pair-programming sessions — team loves them',
          'End-of-sprint mini demos to stakeholders',
          'Retro action item review at the start of every planning session',
          'Daily frontend/backend sync call — keeps alignment tight',
          'Writing unit tests before submitting a PR',
        ],
      },
    ],
    aiSummary:
      'AI Summary — Checkout Squad, Sprint 11 (Start/Stop/Continue, 7 participants):\n\nThe dominant theme is code quality and process discipline. The top "Stop" item — merging without review — combined with the top "Start" item — writing PR descriptions — both point to the same root cause: the team\'s code review culture needs strengthening. These two items should be paired as a single initiative (branch protection + PR template) rather than treated separately.\n\nThe team\'s highest-voted "Continue" item — Thursday pair programming (9 votes, unanimous) — is a high-value practice that should be explicitly protected in sprint planning. It should not be dropped under delivery pressure.\n\n4 action items committed, all with named owners. Recommend opening Sprint 12 by reviewing status of all four before sprint planning begins.',
    seoTitle: 'Start Stop Continue Retrospective Template — Example & Guide',
    seoDescription:
      'Free Start Stop Continue retrospective template with real examples, column descriptions, and an AI-generated summary. Run your next retro in minutes.',
    relatedBlogSlug: 'start-stop-continue-retrospective-example',
    relatedBlogTitle: 'Start Stop Continue Retrospective Example (With AI Summary)',
  },

  // ─────────────────────────────────────────────────────────────────
  // Mad Sad Glad
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'mad-sad-glad',
    name: 'Mad, Sad, Glad',
    icon: '😊',
    tagline: 'Surface team emotions before they become team problems.',
    description:
      'Mad, Sad, Glad is a retrospective format that puts emotional experience at the center. Instead of asking "what should we change?", it asks "how did this sprint make you feel?" This unlocks conversations that process-focused formats often miss — burnout signals, interpersonal friction, and genuine celebrations that get skipped when teams rush to action items.',
    bestFor: [
      'Teams where morale seems low or tension is building',
      'After a particularly stressful or difficult sprint',
      'When previous retros have been too process-heavy and missed the human element',
      'Teams with strong psychological safety and openness to emotional discussion',
      'Rotating in occasionally — every 3–4 sprints — to check team health',
    ],
    columns: [
      {
        name: 'Mad',
        color: '#EF4444',
        colorBg: '#FEE2E2',
        description: 'Things that frustrated or angered the team this sprint.',
        exampleCards: [
          'Requirements changing mid-sprint without impact assessment',
          'Being pulled into 3-4 unplanned meetings per week that kill deep work',
          'No feedback on the A/B test results we shipped 3 sprints ago',
          'Design reviews happening after development has already started',
          'The deployment pipeline takes 20 minutes — context switching nightmare',
        ],
      },
      {
        name: 'Sad',
        color: '#6B7280',
        colorBg: '#F3F4F6',
        description: 'Disappointments, missed opportunities, or things that let the team down.',
        exampleCards: [
          'The activation feature we worked hard on was deprioritized without explanation',
          'We haven\'t had a team social event since Q3 — losing connection as a remote team',
          'Sprint carry-over for the third sprint in a row',
          'Velocity is down 20% but nobody has acknowledged it',
          'We keep identifying the same root causes retro after retro',
        ],
      },
      {
        name: 'Glad',
        color: '#10B981',
        colorBg: '#D1FAE5',
        description: 'Wins, positive experiences, and things the team is proud of.',
        exampleCards: [
          'Referral feature launched — early metrics are strong',
          'New team member onboarded smoothly — pair programming in week 1 made a difference',
          'PM responded to blockers within 1 hour this sprint — felt very supported',
          'Finally deprecated the legacy segment tracking code',
          'Zero production incidents this sprint for the first time in 2 months',
        ],
      },
    ],
    aiSummary:
      'AI Summary — Growth Squad, Sprint 22 (Mad/Sad/Glad, anonymous, 6 participants):\n\nThe emotional temperature of this team is strained. The "Mad" column is dominated by process failures that have repeated for 3+ sprints: mid-sprint requirement changes, meeting overload, and delayed design reviews. The fact that these recur indicates previous action items either weren\'t implemented or didn\'t hold.\n\nThe "Sad" column reveals deeper concerns: deprioritized work without explanation, declining velocity going unacknowledged, and social isolation as a remote team. These are cumulative morale drains.\n\nBright spots are real: the referral launch, strong onboarding, and PM responsiveness show what the team can do when conditions are right. Protect these.\n\nRecommendation: the recurring "Mad" pattern suggests a leadership conversation is overdue around meeting culture and mid-sprint scope changes.',
    seoTitle: 'Mad Sad Glad Retrospective Template — Example & Guide',
    seoDescription:
      'Free Mad Sad Glad retrospective template with real example cards, facilitation tips, and an AI-generated summary. Surface team emotions before they become team problems.',
    relatedBlogSlug: 'mad-sad-glad-retrospective-example',
    relatedBlogTitle: 'Mad Sad Glad Retrospective Example (Real Cards + AI Summary)',
  },

  // ─────────────────────────────────────────────────────────────────
  // 4Ls
  // ─────────────────────────────────────────────────────────────────
  {
    slug: '4ls',
    name: '4Ls Retrospective',
    icon: '📚',
    tagline: 'Liked, Learned, Lacked, Longed For — for when you need to go deeper.',
    description:
      'The 4Ls format — Liked, Learned, Lacked, Longed For — separates emotional experience, knowledge gained, process gaps, and unmet desires into four distinct lenses. This structure produces richer insights than Start/Stop/Continue because it asks different types of questions simultaneously. It\'s especially powerful after major milestones, difficult sprints, or when a team needs to surface systemic issues.',
    bestFor: [
      'After a major product launch or significant milestone sprint',
      'When the team is processing a high-intensity or challenging period',
      'When previous retros have felt surface-level or repetitive',
      'Quarterly retrospectives covering a longer time period',
      'Teams that want to capture both process issues and morale in one session',
    ],
    columns: [
      {
        name: 'Liked',
        color: '#10B981',
        colorBg: '#D1FAE5',
        description: 'Positive experiences, wins, and things the team genuinely enjoyed or appreciated.',
        exampleCards: [
          'The new pipeline architecture — much more maintainable than the old one',
          'Cross-team collaboration with the ML team during the feature build',
          'PM availability this sprint — quick decisions when we were blocked',
          'The blameless post-mortem process we adopted for incidents',
          'Team lunch the week before launch — boosted morale significantly',
        ],
      },
      {
        name: 'Learned',
        color: '#3B82F6',
        colorBg: '#DBEAFE',
        description: 'New knowledge, insights, or discoveries from this sprint.',
        exampleCards: [
          'Streaming architecture requirements need validation before sprint starts, not during',
          'Data schema changes require at least 2 weeks lead time for downstream consumers',
          'Monitoring dashboards should be built before launch, not after the first incident',
          'Pair programming reduces review cycles more than solo work + async review',
          'Feature flagging is cheaper than we thought — should use it more aggressively',
        ],
      },
      {
        name: 'Lacked',
        color: '#EF4444',
        colorBg: '#FEE2E2',
        description: 'What was missing, insufficient, or not available when the team needed it.',
        exampleCards: [
          'Clear data ownership documentation — unclear who owns which tables',
          'A testing environment that mirrors production data size',
          'Agreed SLA definitions for data freshness',
          'Onboarding documentation for new pipeline contributors',
          'Regular sync with the analytics team to catch downstream impact earlier',
        ],
      },
      {
        name: 'Longed For',
        color: '#8B5CF6',
        colorBg: '#EDE9FE',
        description: 'Things the team wishes existed — tools, processes, practices, or support.',
        exampleCards: [
          'Automated data quality checks running on every pipeline job',
          'A data catalog tool to document schemas and ownership',
          'Dedicated 20% time for infrastructure improvements each sprint',
          'Better alerting — we found out about data lag from a stakeholder, not a monitor',
          'A staging environment with production-scale data for realistic testing',
        ],
      },
    ],
    aiSummary:
      'AI Summary — Data Platform, Q1 Milestone Retro (4Ls, 7 participants):\n\nThe team successfully delivered a major pipeline rewrite and values the new architecture. However, this retrospective surfaced a clear pattern: the team lacks the observability and documentation infrastructure to operate confidently at scale.\n\nThree of the four top "Lacked" items and three of the four "Longed For" items point to the same strategic gap: the team is building products without building the supporting systems needed to operate them reliably.\n\nThis is a strategic investment conversation, not just a process fix. Bring the top "Longed For" items — data catalog, automated quality checks, dedicated infrastructure time — into Q2 planning with leadership.\n\nPositive signal: cross-team collaboration and PM availability scored highly. Protect these conditions in Q2.',
    seoTitle: '4Ls Retrospective Template — Liked Learned Lacked Longed For Example',
    seoDescription:
      'Free 4Ls retrospective template with real example cards, column explanations, and an AI-generated summary. Best used after major milestones or challenging sprints.',
    relatedBlogSlug: '4ls-retrospective-example',
    relatedBlogTitle: '4Ls Retrospective Example — Liked, Learned, Lacked, Longed For',
  },

  // ─────────────────────────────────────────────────────────────────
  // Sailboat
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'sailboat',
    name: 'Sailboat Retrospective',
    icon: '⛵',
    tagline: 'A visual metaphor that connects daily friction to your bigger team goals.',
    description:
      'The Sailboat retrospective uses a visual metaphor to give your team a strategic view of where they are and where they\'re going. The sailboat represents your team. Wind fills the sails — what\'s propelling you forward? Anchors drag you back — what\'s slowing progress? Rocks ahead are risks to navigate. The island on the horizon is your goal. This format is ideal for strategic sessions that go beyond the tactical sprint level.',
    bestFor: [
      'Strategic retrospectives — quarterly or post-milestone',
      'When the team needs to reconnect with their larger goals and direction',
      'Teams experiencing a persistent blocker that feels bigger than a sprint issue',
      'Leadership team retrospectives and planning sessions',
      'When you want to surface risks before they become incidents',
    ],
    columns: [
      {
        name: 'Wind (Helping Us)',
        color: '#06B6D4',
        colorBg: '#CFFAFE',
        description: 'What is propelling the team forward? Energy, momentum, and enablers.',
        exampleCards: [
          'Strong product vision — everyone knows what we\'re building and why',
          'New infrastructure that removed the 3-hour deployment bottleneck',
          'Executive buy-in for the platform rewrite — no friction on resources',
          'Team technical skills growing fast — knowledge sharing is working',
          'Close collaboration with design — we\'re shipping more polished features',
        ],
      },
      {
        name: 'Anchor (Holding Back)',
        color: '#6B7280',
        colorBg: '#F3F4F6',
        description: 'What is slowing the team down? Persistent blockers and friction.',
        exampleCards: [
          'Legacy authentication service — every new feature requires a workaround',
          'Unclear ownership between Platform and Product teams on API contracts',
          'Manual QA process that blocks releases for 2-3 days per feature',
          'Tech debt in the payment module — can\'t move fast without breaking things',
          'Too many stakeholders reviewing the same decisions — approval bottleneck',
        ],
      },
      {
        name: 'Rocks (Risks Ahead)',
        color: '#EF4444',
        colorBg: '#FEE2E2',
        description: 'What risks or dangers do you see coming that the team needs to navigate?',
        exampleCards: [
          'Key engineer leaving — significant knowledge is not documented',
          'Third-party payment provider announced API deprecation — 6 months to migrate',
          'Growing technical debt may make Q3 features impossible at current pace',
          'Team burnout risk — velocity has been at 110% for 3 consecutive quarters',
          'Competitor launched a similar feature — pressure to ship faster may increase',
        ],
      },
      {
        name: 'Island (Our Goal)',
        color: '#10B981',
        colorBg: '#D1FAE5',
        description: 'What is the team sailing toward? Goals, outcomes, and the north star.',
        exampleCards: [
          'Ship the new checkout flow to 100% of users by end of Q2',
          'Reduce average support tickets per release by 50%',
          'Achieve 99.9% uptime for the payments service',
          'Build a platform that any engineer can deploy to in under 10 minutes',
          'Be the team other teams want to partner with — quality and speed both',
        ],
      },
    ],
    aiSummary:
      'AI Summary — Platform Squad, Q1 Strategic Retro (Sailboat, 8 participants):\n\nThe team has clear wind in its sails: strong product vision, executive buy-in, and growing technical capability. However, two anchors are dragging disproportionately — the legacy authentication service and the manual QA process. Both have been flagged in previous retrospectives without resolution, suggesting they require dedicated sprint investment rather than being addressed incrementally.\n\nThe most critical rock: key engineer knowledge concentration. If undocumented, this represents a single point of failure that could jeopardize Q2 delivery.\n\nThe island (goal) is clear and shared — the team knows where it\'s going. The strategic priority should be removing the two major anchors in Q2 so the team can reach it.\n\nRecommended: schedule a dedicated "anchor sprint" to tackle the authentication service and QA automation in parallel.',
    seoTitle: 'Sailboat Retrospective Template — Example & Guide',
    seoDescription:
      'Free Sailboat retrospective template with real example cards, metaphor guide, and an AI-generated summary. Best for strategic retrospectives and quarterly reviews.',
  },
];

export function getTemplateDetail(slug: string): TemplateDetail | undefined {
  return templateDetails.find((t) => t.slug === slug);
}
